import { HttpStatusCode } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '@service/auth.service';
import { CustomGraphAccessSettingsService } from '@service/custom-graph-access-settings.service';
import { CustomGraphService } from '@service/custom-graph.service';
import { CustomGraph } from 'ops/common/models/custom-graph';
import {
    first,
    firstValueFrom,
    lastValueFrom,
    Observable,
    skip,
    Subject,
    take,
    takeUntil,
} from 'rxjs';
import _ from 'lodash';
import { customGraphSize, CustomGraphType } from '@models/custom-graph';
import { CustomGraphSocketService } from '@service/custom-graph-socket.service';
import { LeavePageConfirmationComponent } from '@components/leave-page-confirmation/leave-page-confirmation.component';
import { AccessType } from 'ops/common/models/access-settings';
import { TranslateService } from '@ngx-translate/core';
import { GraphsService } from '@service/graphs.service';
import { Graph } from 'ops/common/models/graphs';

@Component({
    selector: 'app-custom-graph-create',
    templateUrl: 'custom-graph-create.component.html',
    styleUrls: ['./custom-graph-create.component.scss'],
    standalone: false,
})
export class CustomGraphCreateComponent {
    readonly GAUGE = CustomGraphType.gauge;
    readonly METER = CustomGraphType.meter;
    readonly INDICATOR = CustomGraphType.indicator;

    @ViewChild('graphContainer') graphContainer: ElementRef;
    @ViewChild('exitConfirmOverlay') exitConfirmOverlay: LeavePageConfirmationComponent;

    initialCustomGraph: any;
    initialUsersAccess: any;
    graphList: (Graph | CustomGraph)[];
    selectedNum: number;
    selectedCoord: {
        x: number;
        y: number;
    };
    isDialogOpen: boolean = false;
    isFromRight: boolean;
    rightWall: number;
    bottomWall: number;
    name: string;
    initName: string;
    isPublicGraph: boolean = true;
    usersAccess = [];
    editCustomGraphId: string = '';
    displayNameFocus = false;
    displayNameError = false;
    filteredGraphList: (Graph | CustomGraph)[] = [];
    titleError: string = this.translateService.instant('custom-graph.create-title-error-message');

    itemList: any[] = new Array(32).fill(null);
    selectedCustomGraph: CustomGraph = null;
    viewInit: boolean = false;

    defaultGraphs: Graph[];

    isGraphSaved: boolean = false;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private authService: AuthService,
        private customGraphService: CustomGraphService,
        private customGraphAccessService: CustomGraphAccessSettingsService,
        private router: Router,
        private graphsService: GraphsService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private customGraphSocketService: CustomGraphSocketService,
        private cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit() {
        this.customGraphSocketService.startSubscriptions();
        const graphs = await this.graphsService.getAll();
        this.defaultGraphs = graphs;

        this.editCustomGraphId = this.route.snapshot.paramMap.get('customGraphId') || '';
        this.usersAccess = await this.customGraphAccessService.getAll(this.editCustomGraphId);
        this.initialUsersAccess = Object.assign([], this.usersAccess);

        this.graphList = [...graphs, ...(await this.customGraphService.getList())];

        if (!this.initName) {
            this.initName = `Custom Graph ${this.graphList.length + 1}`;
            this.name = this.initName;
        }

        this.selectedCustomGraph = await this.customGraphService.getSelectedGraph(
            this.editCustomGraphId ? this.editCustomGraphId : null,
        );
        if (this.selectedCustomGraph) {
            this.itemList = _.cloneDeep(this.selectedCustomGraph.graphList);
            this.isPublicGraph = this.selectedCustomGraph.publicAccess;
            this.name = this.selectedCustomGraph.displayName;

            this.initialCustomGraph = {
                graphList: _.cloneDeep(this.itemList),
                publicAccess: this.isPublicGraph,
                displayName: this.name,
            };
        }

        this.viewInit = true;

        this.cdr.detectChanges();

        this.customGraphSocketService.updateListSubject().subscribe(async (list) => {
            const customGraphList = await this.customGraphService.getList();
            this.graphList = [...this.defaultGraphs, ...customGraphList];
            this.cdr.detectChanges();
        });

        this.authService.currentUser$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async (currentUser) => {
                const currentUserId = currentUser.userId;
                if (
                    this.selectedCustomGraph &&
                    this.selectedCustomGraph.id === this.editCustomGraphId
                ) {
                    if (!currentUserId) {
                        this.router.navigate(['/ops', 'graphs']);
                        return;
                    }
                    const hasNoAccessOrViewOnly = this.usersAccess.some(
                        (usersAccess) =>
                            usersAccess.roleId === currentUserId &&
                            (usersAccess.accessLevel === AccessType.NoAccess ||
                                usersAccess.accessLevel === AccessType.View),
                    );
                    if (hasNoAccessOrViewOnly) {
                        this.router.navigate(['/ops', 'graphs']);
                        return;
                    }
                }
                this.filteredGraphList = [];
                if (!currentUserId) {
                    this.graphList.forEach((customGraph) => {
                        if (this.isCustomGraphType(customGraph) && customGraph.publicAccess) {
                            this.filteredGraphList.push(customGraph);
                        } else if (!this.isCustomGraphType(customGraph)) {
                            this.filteredGraphList.push(customGraph);
                        }
                    });
                } else {
                    for (let i = 0; i < this.graphList.length; i++) {
                        let graph = this.graphList[i];
                        if (this.isCustomGraphType(graph) && graph.publicAccess) {
                            this.filteredGraphList.push(graph);
                        } else if (this.isCustomGraphType(graph)) {
                            const accessLevel = await this.customGraphAccessService.getAll(
                                graph.id,
                            );
                            accessLevel.forEach((usersAccess) => {
                                if (
                                    usersAccess.roleId === currentUserId &&
                                    usersAccess.accessLevel !== AccessType.NoAccess
                                ) {
                                    this.filteredGraphList.push(graph);
                                }
                            });
                        } else if (!this.isCustomGraphType(graph)) {
                            this.filteredGraphList.push(graph);
                        }
                    }
                }
                this.cdr.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.customGraphSocketService.destroySubscriptions();
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    setCustomGraph = (customGraph: any) => {
        if (customGraph == null) {
            this.itemList[this.selectedNum] = null;
            return;
        }

        let endX = Math.floor(this.selectedCoord.x);
        let endY = Math.floor(this.selectedCoord.y);

        if (customGraph?.type) {
            endX += customGraphSize[customGraph?.type].width;
            endY += customGraphSize[customGraph?.type].height;
        }

        this.itemList[this.selectedNum] = {
            ...customGraph,
            ...this.selectedCoord,
            index: this.selectedNum,
            endX,
            endY,
        };

        this.itemList = _.cloneDeep(this.itemList);
    };

    customGraphClick(e: MouseEvent, customGraph: any, index: number) {
        e.stopPropagation();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const parentRect = this.graphContainer.nativeElement.getBoundingClientRect();
        this.selectedCoord = {
            x: rect.left - parentRect.left,
            y: rect.top - parentRect.top,
        };

        this.selectedNum = index;
        this.checkDialogFromRight();
        this.isDialogOpen = true;
    }

    emptyGraphClick(event: MouseEvent, num: number) {
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const parentRect = this.graphContainer.nativeElement.getBoundingClientRect();
        this.selectedCoord = {
            x: rect.left - parentRect.left,
            y: rect.top - parentRect.top,
        };

        this.selectedNum = num;
        this.checkDialogFromRight();
        this.isDialogOpen = true;
    }

    checkDialogFromRight() {
        const row = Math.floor(this.selectedNum / 8);
        const col = this.selectedNum % 8;
        if (row <= 3 && col <= 3) {
            this.isFromRight = false;
        } else {
            this.isFromRight = true;
        }
    }

    isCustomGraphTypeGauge(customGraph) {
        return customGraph?.type === this.GAUGE;
    }

    isCustomGraphTypeMeter(customGraph) {
        return customGraph?.type === this.METER;
    }

    isCustomGraphTypeIndicator(customGraph) {
        return customGraph?.type === this.INDICATOR;
    }

    async submit(event: MouseEvent) {
        event.preventDefault();
        let customGraphId;
        let graphList = this.itemList.map((graph) => {
            const item =
                graph && graph.channel ? { ...graph, channel: { id: graph.channel.id } } : null;
            return item;
        });
        try {
            if (this.editCustomGraphId) {
                await this.customGraphAccessService.update(this.usersAccess);
                await this.customGraphService.update(this.editCustomGraphId, {
                    name: this.name,
                    graphList: this.itemList,
                    publicAccess: this.isPublicGraph,
                });
                customGraphId = this.editCustomGraphId;
            } else {
                const newCustomGraph = await this.customGraphService.create(
                    this.name,
                    graphList,
                    this.isPublicGraph,
                );
                await this.customGraphAccessService.create(this.usersAccess, newCustomGraph.id);
                customGraphId = newCustomGraph.id;
            }
            this.isGraphSaved = true;
            this.router.navigate(['/ops', 'graphs', customGraphId]);
        } catch (err) {
            this.isGraphSaved = false;
            if (err.status == HttpStatusCode.Unauthorized) {
                this.authService.signOut();
                this.authService.showSignInOverlay();
                const loggedInUser = await firstValueFrom(
                    this.authService.currentUser$.pipe(skip(1), first()),
                );
                if (loggedInUser.token && loggedInUser.userId) {
                    this.submit(event);
                }
            } else {
                console.error(err);
            }
        }
    }

    async cancel() {
        if (this.filteredGraphList.length === 0) {
            return;
        }

        await this.router.navigate(['/ops', 'graphs', this.editCustomGraphId]);
    }

    closeDialog() {
        this.selectedNum = undefined;
        this.isDialogOpen = false;
    }

    handleAccessChanged(isPublic) {
        this.isPublicGraph = isPublic;
    }

    handleUserAccessChanged(newUsersPermissions) {
        if (this.usersAccess.length > 0) {
            this.usersAccess.map((access) => {
                if (access.roleId in newUsersPermissions) {
                    access.accessLevel = newUsersPermissions[access.roleId];
                }
            });
        } else {
            for (let user in newUsersPermissions) {
                this.usersAccess.push({
                    roleId: user,
                    accessLevel: newUsersPermissions[user],
                });
            }
        }
    }

    displayNameClick(event: MouseEvent) {
        event.stopImmediatePropagation();
        this.displayNameFocus = true;
        document.addEventListener('click', this.displayNameClose);
    }

    displayNameClose = () => {
        this.displayNameFocus = false;
        document.removeEventListener('click', this.displayNameClose);
    };

    getTitleList() {
        return this.graphList.map((customGraph) => {
            if (this.isCustomGraphType(customGraph)) {
                return customGraph.displayName;
            } else {
                return customGraph.title;
            }
        });
    }

    selectDisplayName(name) {
        this.name = name;
    }

    titleIsRepeated() {
        let checkError = false;
        if (this.graphList) {
            for (let i = 0; i < this.graphList.length; i++) {
                const graph = this.graphList[i];
                let name = '';
                if (this.isCustomGraphType(graph)) {
                    name = graph.displayName;
                } else {
                    name = graph.title;
                }
                if (this.name === name) {
                    if (graph.id === this.editCustomGraphId) {
                        checkError = false;
                    } else {
                        checkError = true;
                        break;
                    }
                }
            }
        }
        return checkError;
    }

    checkEditRequiredConditions() {
        return (
            !this.name ||
            (!this.checkHasChanged() &&
                _.isEqual(this.name, this.initialCustomGraph?.displayName)) ||
            this.titleIsRepeated() ||
            this.isGraphListNull()
        );
    }

    checkCreateRequiredConditions() {
        return !this.name || this.isGraphListNull() || this.titleIsRepeated();
    }

    checkHasChanged() {
        if (
            this.editCustomGraphId &&
            this.itemList.length !== 0 &&
            this.initialCustomGraph?.graphList
        ) {
            if (
                !_.isEqual(this.initialCustomGraph.graphList, this.itemList) ||
                !_.isEqual(this.initialUsersAccess, this.usersAccess) ||
                this.initialCustomGraph.publicAccess !== this.isPublicGraph ||
                this.initialCustomGraph.displayName !== this.name
            ) {
                return true;
            }

            return false;
        } else if (!this.editCustomGraphId) {
            if (
                this.initName !== this.name ||
                this.itemList.filter((item) => item).length != 0 ||
                !this.isPublicGraph
            ) {
                return true;
            }
        } else {
            return false;
        }
    }

    isGraphListNull() {
        if (this.itemList.length !== 0) {
            const checkGraphList = this.itemList.filter((graphList) => !graphList?.type);
            if (checkGraphList.length === this.itemList.length) {
                return true;
            }
        }
        return false;
    }

    isCustomGraphType(obj: any): obj is CustomGraph {
        return typeof obj?.displayName === 'string';
    }

    removeItem(item) {
        if (!item) return;
        if (!item.index && item.index != 0) return;
        this.itemList[item.index] = null;
        this.itemList = _.cloneDeep(this.itemList);
    }

    static CanDeactivate(
        component: CustomGraphCreateComponent,
        currentRoute: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState: RouterStateSnapshot,
    ): boolean | Observable<boolean> {
        if (component.isGraphSaved) return true;
        if (!component.checkHasChanged()) return true;

        component.exitConfirmOverlay.setVisible(true);
        component.cdr.markForCheck();
        const shouldClosePage = component.exitConfirmOverlay.onOptionSelected.asObservable();
        return shouldClosePage;
    }
}
